import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    useTheme as useMuiTheme,
    Toolbar,
    CircularProgress,
    Drawer,
    Grid,
    Stack,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    Select,
    FormControl,
    InputLabel,
    Avatar,
    Chip,
    TextField,
    Tooltip,
    Badge
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Mail as MailIcon,
    ShoppingCart as CartIcon,
    Storage as DatabaseIcon,
    ChatBubbleOutline as SlackIcon,
    InboxOutlined as InboxIcon,
    TimerOutlined as TimerIcon,
    Bolt as ZapIcon,
    SmartToyOutlined as BotIcon,
    CloudQueue as CloudIcon,
    TrendingUp,
    AppsOutlined as AppsOutlinedIcon,
    Layers as LayersIcon,
    Checklist as ChecklistIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Sync as SyncIcon,


    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    ChevronRight,
    FilterList,
    MoreVert,
    Assignment as TaskIcon,
    ThumbUp as ApprovalIcon,
    ExpandLess,
    Analytics as AnalyticsIcon,
    Source as SourceIcon,
    Settings as SettingsIcon,
    PlayCircleOutline as PlayCircleOutlineIcon
} from '@mui/icons-material';
import ReactWordcloud from 'react-wordcloud';
import { Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { STORAGE_KEYS } from '../helpers/constants';
import { getRuntimeEnv } from '../config/runtimeConfig';
import AclAccessControlDashboard from './AclAccessControlDashboard';
import TrustedAdvisorDashboard from './TrustedAdvisorDashboard';
import {
    getTableHeaderStyles,
    getTableContainerStyles,
    getTableRowStyles,
    getTableCellStyles
} from '../styles/tableStyles';
import {
    getThroughputMetrics,
    getStrategicInsights,
    getBusinessOutcomeMetrics,
    getOverviewMetrics,
    getTasksAndApprovalsMetrics,
    getTimelineMetrics,
    getAppUsageMetrics,
    getFlowRunMetrics,
    ThroughputMetricsResponse,
    StrategicInsightsResponse,
    BusinessOutcomeMetricsResponse,
    OverviewMetricsResponse,
    TasksAndApprovalsMetricsResponse,
    TimelineMetricsResponse,
    AppUsageMetricsResponse,
    FlowRunMetricsResponse
} from '../services/channelDashboardService';
import ChannelDashboardTimelineChart from './ChannelDashboardTimelineChart';


// --- Components (use MUI theme palette for light/dark consistency) ---

export const StatMiniCard = ({ title, content, icon }: { title: string, content: React.ReactNode, icon?: React.ReactNode }) => {
    return (
    <Paper elevation={0} sx={{ 
        p: 2, 
        border: '1px solid',
        borderColor: 'divider', 
        borderRadius: '12px',
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'flex-start',
        height: '100%',
        minHeight: '100px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }
    }}>
        <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            letterSpacing: '0.05em', 
            mb: (title === "Sync Status" || title === "Active Integrations") ? '20px !important' : 1, 
            fontSize: '0.875rem !important' 
        }}>
            {title}
        </Typography>
        <Box>
            {React.isValidElement(content) ? React.cloneElement(content as React.ReactElement<any>, { 
                sx: { 
                     ...((content as React.ReactElement<any>).props.sx || {}),
                     fontSize: (title === "Active Users" || title === "Global MTTR" || title === "Open Tasks" || title === "Approvals" || title === "Workflow Runs") ? '2.125rem !important' : (title === "Sync Status") ? '1rem !important' : (title === "Active Integrations") ? undefined : '1.5rem !important',
                     fontWeight: (title === "Active Users" || title === "Global MTTR" || title === "Sync Status" || title === "Open Tasks" || title === "Approvals" || title === "Workflow Runs") ? '700 !important' : ((content as React.ReactElement<any>).props.sx as any)?.fontWeight
                }
            }) : content}
        </Box>
    </Paper>
); };

// Deferred mount so react-wordcloud's ref has a parent with dimensions (avoids "reading '0' of undefined")
const EmailTopicsWordCloud = (props: { words: { text: string; value: number }[]; options: object; callbacks: object; size: [number, number]; minSize: [number, number] }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const t = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(t);
    }, []);
    if (!mounted) return <Box sx={{ width: '100%', height: 220, minHeight: 220 }} />;
    return (
        <ReactWordcloud
            words={props.words}
            options={props.options}
            callbacks={props.callbacks}
            size={props.size}
            minSize={props.minSize}
        />
    );
};

export const DashboardCard = ({ title, icon, children, action, className }: { title: string, icon?: React.ReactNode, children: React.ReactNode, action?: React.ReactNode, className?: string }) => {
    return (
    <Paper elevation={0} className={className} sx={{ 
        p: 2.5, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        minHeight: '240px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.05em', fontSize: '0.875rem !important' }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                {action}
                {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { sx: { fontSize: 24 } }) : icon}
            </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {children}
        </Box>
    </Paper>
); };

const HalfCard = ({ title, icon, children, action }: { title: string, icon: React.ReactNode, children: React.ReactNode, action?: React.ReactNode }) => {
    return (
    <Paper elevation={0} sx={{ 
        p: 2.5, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        bgcolor: 'background.paper',
        justifyContent: 'center'
    }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', minHeight: '40px' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.05em', fontSize: '0.875rem !important' }}>
                {title}
            </Typography>
            <Box sx={{ color: 'text.secondary' }}>
                {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { sx: { fontSize: 24 } }) : icon}
            </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {children}
        </Box>
    </Paper>
); };

const TopicsList = ({ topics }: { topics: string | number }) => {
    const [expanded, setExpanded] = useState(false);

    // Parse topics: assume it can be a comma-separated string or already an array (though type says number/string)
    // If it's a number, just show it as text (fallback)
    const topicArray = React.useMemo(() => {
        if (typeof topics === 'string') {
            return topics.split(',').map(t => t.trim()).filter(Boolean);
        }
        if (Array.isArray(topics)) {
            return topics;
        }
        return [String(topics)];
    }, [topics]);

    const displayTopics = expanded ? topicArray : topicArray.slice(0, 2);
    const remainingCount = topicArray.length - 2;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {displayTopics.map((topic, index) => (
                <Chip
                    key={index}
                    label={topic}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        maxWidth: '100%'
                    }}
                />
            ))}
            {!expanded && remainingCount > 0 && (
                <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(true);
                    }}
                    sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: 'primary.main',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.24) }
                    }}
                />
            )}
             {/* Optional: Collapse button if expanded and was truncated */}
             {expanded && topicArray.length > 2 && (
                 <Chip
                    icon={<ExpandLess sx={{ fontSize: '1rem !important' }} />}
                    label=""
                    size="small"
                     onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(false);
                    }}
                     sx={{
                        height: 20,
                        width: 24,
                        fontSize: '0.65rem',
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '& .MuiChip-label': { padding: 0 },
                        '&:hover': { bgcolor: 'action.hover' },
                        '& .MuiChip-icon': { margin: '0 !important' } 
                    }}
                 />
             )}
        </Box>
    );
};

const CustomApprovalIcon = () => (
    <Box sx={{ 
      width: '24px', 
      height: '24px', 
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Box sx={{ 
        width: '16px', 
        height: '8px', 
        borderLeft: '4px solid currentColor',
        borderBottom: '4px solid currentColor',
        transform: 'rotate(-45deg) translateY(-0.5px)',
        borderRadius: '1px'
      }} />
    </Box>
);


const CenteredLoader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 40, width: '100%' }}>
        <CircularProgress size={24} sx={{ color: 'primary.main' }} />
    </Box>
);

interface ChannelDashboardProps {
    channelId: string;
    channelName?: string;
    workspaceTag?: string;
    open: boolean;
    onClose: () => void;
    /** Switch to the Data Sources panel (channel home view is Dashboard / Data Sources / Settings only) */
    onOpenDataSources?: () => void;
    dataSourceCount?: number;
    /** Switch to the Settings panel */
    onOpenSettings?: () => void;
    /** PR #665 review (M2) — when set to 'workflowRuns' while `open` is true, scrolls straight to
     * the Workflow Runs by Flow breakdown once the drawer has mounted, instead of opening at its
     * default scroll position. Lets a caller outside this Dashboard (e.g. the Business Rules
     * Workflow canvas's "Runs: N" label) actually land on that section, not just open the
     * Dashboard in general. Self-clears via onScrolledToSection once consumed. */
    scrollToOnOpen?: 'workflowRuns' | null;
    /** Fired once the scrollToOnOpen request above has been acted on, so the caller can reset it
     * (otherwise closing and reopening this Dashboard normally would keep re-triggering the same
     * scroll). */
    onScrolledToSection?: () => void;
}

const ChannelDashboard: React.FC<ChannelDashboardProps> = ({ channelId, channelName, workspaceTag, open, onClose, onOpenDataSources, dataSourceCount, onOpenSettings, scrollToOnOpen, onScrolledToSection }) => {
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();
    const { isDarkMode } = useTheme();
    const muiTheme = useMuiTheme();
    const palette = muiTheme.palette;

    // Filters & State
    const [timeFilter, setTimeFilter] = useState('All');
    const [timeFilterMenuAnchor, setTimeFilterMenuAnchor] = useState<null | HTMLElement>(null);
    const [loading, setLoading] = useState(false);

    // Prism7 dashboard view selector — temporary, gated to a single channel via env var.
    // Options mirror the ACL Access Control / Trusted Advisor tabs from streamlit_app.py.
    type DashboardView = 'channel_ops' | 'trusted_advisor' | 'acl';
    const [dashboardView, setDashboardView] = useState<DashboardView>('channel_ops');
    const prism7DashboardChannelEmail = getRuntimeEnv().VITE_PRISM7_DASHBOARD_CHANNEL_EMAIL || '';
    // B-1: UX-only gate — controls which dropdown tab is visible in the UI.
    // Authorization is enforced server-side by the /shay/dashboard/* endpoints,
    // which return 403 for any caller the backend does not authorise.
    // This client-side string comparison must NOT be treated as a security boundary.
    // Supports comma-separated list of emails so multiple channels can be gated.
    const prism7AllowedEmails = prism7DashboardChannelEmail.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    const showPrism7DashboardSelector = Boolean(
        channelName && prism7AllowedEmails.length > 0 &&
        prism7AllowedEmails.includes(channelName.toLowerCase())
    );
    
    // Data State
    const [throughputData, setThroughputData] = useState<ThroughputMetricsResponse | null>(null);
    const [insightsData, setInsightsData] = useState<StrategicInsightsResponse | null>(null);
    const [businessOutcomeData, setBusinessOutcomeData] = useState<BusinessOutcomeMetricsResponse | null>(null);
    const [overviewData, setOverviewData] = useState<OverviewMetricsResponse | null>(null);
    const [tasksApprovalsData, setTasksApprovalsData] = useState<TasksAndApprovalsMetricsResponse | null>(null);
    const [timelineData, setTimelineData] = useState<TimelineMetricsResponse | null>(null);
    const [appUsageData, setAppUsageData] = useState<AppUsageMetricsResponse | null>(null);
    const [flowRunData, setFlowRunData] = useState<FlowRunMetricsResponse | null>(null);

    // Modal State
    const [drillDownType, setDrillDownType] = useState<string | null>(null);
    const [userContributionFilter, setUserContributionFilter] = useState<'highest' | 'lowest'>('highest');
    
    // Custom Date Range State
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Helper for Time Filter
    const getApiTimeFilter = (uiFilter: string) => {
        switch (uiFilter) {
            case 'All': return 'all';
            case 'Today': return 'today';
            case 'Yesterday': return 'yesterday';
            case 'Last 24 hrs': return 'last_24_hrs';
            case 'Last 48 hrs': return 'last_48_hrs';
            case 'Last 7 Days': return 'last_7_days';
            case 'Last 30 Days': return 'last_30_days';
            case 'This Month': return 'this_month';
            case 'Last Month': return 'last_month';
            default: 
                if (uiFilter.startsWith('Custom:')) return 'custom';
                return 'today';
        }
    };

    const isFetching = React.useRef(false);

    // business_rules_workflow_count — lets the "Workflow Runs" stat tile's number scroll straight
    // down to the per-flow breakdown list at the bottom of this grid, and lets the Business Rules
    // Workflow tab's canvas-header "Runs: N" label deep-link here from a different drawer entirely
    // (it just opens this dashboard; the admin lands on the same view either way).
    const workflowRunsSectionRef = React.useRef<HTMLDivElement>(null);
    const scrollToWorkflowRuns = () => {
        workflowRunsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const fetchDashboardMetrics = async () => {
        if (!channelId || !user) return;
        if (isFetching.current) return;

        isFetching.current = true;
        setLoading(true);
        try {
            const apiTimeFilter = getApiTimeFilter(timeFilter);
            
            let startDate: string | undefined;
            let endDate: string | undefined;

            if (apiTimeFilter === 'custom' && timeFilter.startsWith('Custom:')) {
                // timeFilter format: "Custom: YYYY-MM-DD to YYYY-MM-DD"
                const parts = timeFilter.replace('Custom: ', '').split(' to ');
                if (parts.length === 2) {
                    startDate = parts[0];
                    endDate = parts[1];
                }
            }

            const [throughput, insights, outcomes, overview, tasks, timeline, appUsage, flowRuns] = await Promise.all([
                getThroughputMetrics(channelId, apiTimeFilter, startDate, endDate),
                getStrategicInsights(channelId, apiTimeFilter, startDate, endDate),
                getBusinessOutcomeMetrics(channelId, apiTimeFilter, startDate, endDate, userContributionFilter),
                getOverviewMetrics(channelId),
                getTasksAndApprovalsMetrics(channelId, apiTimeFilter, startDate, endDate),
                getTimelineMetrics(channelId, apiTimeFilter, startDate, endDate),
                getAppUsageMetrics(channelId, apiTimeFilter, startDate, endDate),
                // Isolated catch: a flow-runs failure must never block the other 7 metrics above.
                getFlowRunMetrics(channelId, apiTimeFilter, startDate, endDate).catch((err) => {
                    console.error('Get flow run metrics error:', err);
                    return null;
                })
            ]);

            setThroughputData(throughput);
            setInsightsData(insights);
            setBusinessOutcomeData(outcomes);
            setOverviewData(overview);
            setTasksApprovalsData(tasks);
            setTimelineData(timeline);
            setAppUsageData(appUsage);
            setFlowRunData(flowRuns);

        } catch (error: any) {
            console.error("Failed to fetch dashboard metrics", error);
             setThroughputData(null);
             setInsightsData(null);
             setBusinessOutcomeData(null);
             setOverviewData(null);
             setTasksApprovalsData(null);
             setTimelineData(null);
             setAppUsageData(null);
             setFlowRunData(null);
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    };

    useEffect(() => {
        if (open) {
            fetchDashboardMetrics();
            
            // Sidebar Interaction Logic (Matching ChannelSettings)
            // Use setTimeout to ensure Modal is rendered
            const timer = setTimeout(() => {
                // Find all Modal roots and check which one contains our drawer
                const modalRoots = document.querySelectorAll('.MuiModal-root');
                modalRoots.forEach((modalRoot) => {
                    const drawer = modalRoot.querySelector('.channel-dashboard-drawer');
                    if (drawer) {
                        (modalRoot as HTMLElement).style.pointerEvents = 'none';
                        // Re-enable pointer events on the drawer and its paper
                        (drawer as HTMLElement).style.pointerEvents = 'auto';
                        const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper') as HTMLElement;
                        if (drawerPaper) {
                            drawerPaper.style.pointerEvents = 'auto';
                        }
                    }
                });
            }, 0);
            return () => clearTimeout(timer);
        } else {
             // Reset time filter when dashboard closes
             // (scrollToOnOpen effect below handles the drill-down-from-elsewhere case separately)
             setTimeFilter('All');
             // Reset filter on close/unmount
             setUserContributionFilter('highest');
             // Reset dashboard view selector when dashboard closes
             setDashboardView('channel_ops');
        }
    }, [channelId, timeFilter, user?.id, open]);

    // PR #665 review (M2) — actually implements the "click Runs: N in the canvas header jumps to
    // the breakdown table" behavior that was previously only described in comments (onOpenDashboard
    // just opened this Dashboard at its default scroll position, with nothing connecting it to
    // scrollToWorkflowRuns). Delayed so the Drawer's open transition/content mount settle first
    // (same timing idiom as the pointer-events fix above); self-clears via onScrolledToSection so
    // a later, ordinary open of this Dashboard (e.g. from its own header icon) doesn't re-trigger it.
    useEffect(() => {
        if (!open || scrollToOnOpen !== 'workflowRuns') return;
        const timer = setTimeout(() => {
            scrollToWorkflowRuns();
            onScrolledToSection?.();
        }, 150);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, scrollToOnOpen]);


    // Also re-fetch if userContributionFilter changes, but only that part technically needed. 
    // For simplicity, re-fetching all or we could separate the call.
    useEffect(() => {
        if (!open || !channelId || !user) return;
        const fetchUserContribution = async () => {
             const apiTimeFilter = getApiTimeFilter(timeFilter);
             let startDate: string | undefined;
             let endDate: string | undefined;

             if (apiTimeFilter === 'custom' && timeFilter.startsWith('Custom:')) {
                 const parts = timeFilter.replace('Custom: ', '').split(' to ');
                 if (parts.length === 2) {
                     startDate = parts[0];
                     endDate = parts[1];
                 }
             }

            try {
                const outcomes = await getBusinessOutcomeMetrics(channelId, apiTimeFilter, startDate, endDate, userContributionFilter);
                setBusinessOutcomeData(outcomes);
            } catch (error) {
                console.error("Failed to fetch user contribution metrics", error);
            }
        };
        fetchUserContribution();
    }, [userContributionFilter, channelId, user?.id]); // Intentionally not including timeFilter here as the main fetch handles it, but if user changes sort, we need to refresh.

    // Sync Status Helper
    const getSyncStatusInfo = () => {
        // Use Overview API if available
        if (overviewData?.sync_status) {
             const { status, is_connected } = overviewData.sync_status;
             if (!is_connected) {
                 return {
                    status: status,
                    color: palette.error.main,
                    textColor: palette.error.main,
                    icon: <ErrorIcon />
                };
             }
             if (status === 'Connected' || status === 'Active' || status === 'Successful') {
                  return {
                    status: status,
                    color: palette.success.main,
                    textColor: palette.success.main,
                    icon: <CheckCircleIcon />
                };
             }
             return {
                status: status,
                color: palette.primary.main,
                textColor: palette.text.primary,
                icon: <SyncIcon />
            };
        }

        return {
            status: 'Not Synced',
            color: palette.divider,
            textColor: palette.text.secondary,
            icon: <ErrorIcon />
        };
    };

    const syncStatusInfo = getSyncStatusInfo();

    // Formatters
    const formatMTTR = (minutes: number = 0) => {
        if (minutes > 60) {
            const h = Math.floor(minutes / 60);
            const m = Math.floor(minutes % 60);
            return `${h}h ${m}m`;
        }
        const m = Math.floor(minutes);
        const s = Math.round((minutes - m) * 60);
        return `${m}m ${s}s`;
    };

    // Same "Xm Ys" convention as formatMTTR above, just fed milliseconds (avg_duration_ms) instead
    // of minutes — sub-minute runs show one decimal of seconds instead of rounding to whole minutes.
    const formatFlowRunDuration = (ms: number = 0) => {
        if (ms >= 60000) {
            const totalSeconds = ms / 1000;
            const m = Math.floor(totalSeconds / 60);
            const s = Math.round(totalSeconds % 60);
            return `${m}m ${s}s`;
        }
        return `${(ms / 1000).toFixed(1)}s`;
    };

    // Prepare Data for Charts
    const emailDistributionData = throughputData?.email_distribution ? [
        { name: 'Inbox', value: throughputData.email_distribution.inbox.count, color: palette.primary.main },
        { name: 'Spam', value: throughputData.email_distribution.spam.count, color: palette.grey[500] },
        { name: 'No-Reply', value: throughputData.email_distribution.no_reply.count, color: palette.grey[400] },
    ] : [];

    const totalEmails = emailDistributionData.reduce((acc, curr) => acc + curr.value, 0);

    const shayQueriesData = throughputData?.shay_queries?.top_users.map(u => ({
        name: u.user_name.split(' ')[0], // First name only for chart
        value: u.query_count
    })) || [];

    const topicCloudData = insightsData?.topic_cloud || [];

    const emergingTopicsData = insightsData?.emerging_topics || [];

    // User contribution from API (prism7_queries included in response; no merge with throughput needed)
    const userContributionData = React.useMemo(() => {
        const users = [...(businessOutcomeData?.user_contribution?.users || [])];
        // If only 1 or 0 users, default strictly to highest logic (no sorting needed visually, but good for consistency)
        if (users.length <= 1 && userContributionFilter !== 'highest') {
             // Side-effect to reset filter if data changes to single item? 
             // React checking: better just handle sort logic safely or allow 'lowest' on single item.
             // Given constraint: "Highest and Lowest filter bar need to hide", so value doesn't matter much visually.
        }

        if (userContributionFilter === 'lowest') {
            return users.sort((a, b) => (a.drafts_viewed + a.messages_sent) - (b.drafts_viewed + b.messages_sent));
        }
        return users.sort((a, b) => (b.drafts_viewed + b.messages_sent) - (a.drafts_viewed + a.messages_sent));
    }, [businessOutcomeData, userContributionFilter]);    
    
    // Overview Metrics
    const activeUsers = overviewData?.active_users || { active_count: 0, total_count: 0, formatted: '0 / 0' };
    const globalMTTR = overviewData?.global_mttr || { value_hours: 0, value_minutes: 0, formatted: '0h 0m', total_responses: 0 };
    
    // App Usage Metrics
    const appUsageList = appUsageData?.category_wise_usage.map(cat => ({
        name: cat.category,
        value: cat.total_invocations_percentage,
        // Helper to assign colors based on category if needed, or rotate colors
        color: palette.primary.main 
    })) || [];
    
    const appActionsList = appUsageData?.app_tool_usage.map(tool => ({
        tool: tool.app_name,
        action: tool.tool_name,
        count: tool.invocation_count
    })) || [];

    const totalAppIntegrationCount = appUsageData?.total_app_integrations || 0;

    // Tasks & Approvals
    const totalOpenTasks = tasksApprovalsData?.tasks?.total_open_tasks || 0;
    const totalPendingApprovals = tasksApprovalsData?.approvals?.total_pending_approvals || 0;
    const totalWorkflowRuns = (flowRunData?.flow_runs || []).reduce((sum, flow) => sum + (flow.run_count || 0), 0);
    // Highest-run-count first, so the admin sees which workflows are actually firing at a glance.
    const sortedFlowRuns = [...(flowRunData?.flow_runs || [])].sort((a, b) => (b.run_count || 0) - (a.run_count || 0));
    
    const taskUsersData = tasksApprovalsData?.tasks?.tasks_by_user.map(u => ({
        name: u.user_name.split(' ')[0],
        value: u.task_count
    })) || [];

    const approvalUsersData = tasksApprovalsData?.approvals?.approvals_by_user.map(u => ({
        name: u.user_name.split(' ')[0],
        value: u.approval_count
    })) || [];
    
    // Timeline Data
    const formattedTimelineData = timelineData?.timeline_data.map(d => ({
        time: new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), // Simplified time
        fullDate: d.timestamp,
        inbox: d.inbox_count,
        spam: d.spam_count,
        noreply: d.no_reply_count
    })) || [];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            /* Custom Styles for Sidebar Interaction (Matching ChannelSettings) */
            sx={{
                zIndex: 1300,
                left: '60px',
                width: 'calc(100% - 60px)',
                pointerEvents: 'auto',
                '& .MuiBackdrop-root': {
                    display: 'none !important',
                },
                '& .MuiDrawer-paper': {
                    width: 'calc(100% - 60px)',
                    height: '100%',
                    left: '0 !important',
                    right: 'auto !important',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    // fontFamily: "'Inter', -ui, sans-serif",
                    zIndex: 1300,
                    pointerEvents: 'auto',
                },
                '& .MuiTypography-root': {
                    // fontFamily: "'Inter', system-ui, sans-serif"
                }
            }}
            hideBackdrop={true}
            className="channel-dashboard-drawer"
            ModalProps={{
                disableEnforceFocus: true,
                disableAutoFocus: true,
                disableRestoreFocus: true,
                style: {
                    pointerEvents: 'none',
                },
            }}
        >
            <style>
                {`
                    body.sidebar-closed .channel-dashboard-drawer .MuiDrawer-paper {
                        width: calc(100% - 60px) !important;
                        left: 60px !important;
                    }
                    body.sidebar-open .channel-dashboard-drawer .MuiDrawer-paper {
                        width: calc(100% - 240px) !important;
                        left: 240px !important;
                    }
                    /* Ensure backdrop is completely hidden */
                    .MuiModal-root:has(.channel-dashboard-drawer) .MuiBackdrop-root,
                    .MuiModal-root .channel-dashboard-drawer ~ .MuiBackdrop-root {
                        display: none !important;
                    }
                    /* Disable pointer events on Modal root to allow sidebar clicks */
                    .MuiModal-root:has(.channel-dashboard-drawer),
                    .MuiModal-root[class*="channel-dashboard"] {
                        pointer-events: none !important;
                    }
                    /* Position Modal root container to not overlap sidebar */
                    body.sidebar-closed .MuiModal-root:has(.channel-dashboard-drawer) {
                        left: 60px !important;
                        width: calc(100% - 60px) !important;
                        clip-path: inset(0 0 0 0);
                    }
                    body.sidebar-open .MuiModal-root:has(.channel-dashboard-drawer) {
                        left: 240px !important;
                        width: calc(100% - 240px) !important;
                        clip-path: inset(0 0 0 0);
                    }
                    /* Re-enable pointer events on the drawer and its paper */
                    .MuiModal-root:has(.channel-dashboard-drawer) .channel-dashboard-drawer,
                    .MuiModal-root .channel-dashboard-drawer {
                        pointer-events: auto !important;
                    }
                    .MuiModal-root:has(.channel-dashboard-drawer) .MuiDrawer-paper,
                    .MuiModal-root .channel-dashboard-drawer .MuiDrawer-paper {
                        pointer-events: auto !important;
                    }
                    /* Ensure sidebar is clickable - raise its z-index above modal when dashboard open */
                    body:has(.channel-dashboard-drawer) .MuiDrawer-root[class*="MuiDrawer-permanent"] {
                        z-index: 1400 !important;
                    }
                `}
            </style>
            {/* Main Content Wrapper - Flex Column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                
                {/* Header - Matching ChannelSettings Style */}
                <Toolbar sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    px: 3, 
                    py: 1, 
                    borderBottom: `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: muiTheme.palette.background.paper,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1301,
                    minHeight: '64px !important',
                    flexShrink: 0
                }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: muiTheme.palette.text.primary }}>
                            {/* Title Updated to Dashboard */}
                            Dashboard
                        </Typography>
                        {channelName && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Channel: <Box component="span" sx={{ fontWeight: 'bold' }}>{channelName}</Box>
                                {workspaceTag && (
                                    <>
                                        <Box component="span" sx={{ ml: '5px' }}>|</Box>
                                        <Box component="span">Workspace: <Box component="span" sx={{ fontWeight: 'bold' }}>{workspaceTag}</Box></Box>
                                    </>
                                )}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Dashboard">
                            <IconButton size="small" disabled>
                                <AnalyticsIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                        {onOpenDataSources && (
                            <Tooltip title="Data Sources">
                                <IconButton size="small" onClick={onOpenDataSources}>
                                    <Badge badgeContent={dataSourceCount || 0} color="primary">
                                        <SourceIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        )}
                        {onOpenSettings && (
                            <Tooltip title="Settings">
                                <IconButton size="small" onClick={onOpenSettings}>
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Toolbar>

                {/* Content Scroll Area */}
                <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1, width: '100%' }}>
                
                {/* 1. Stats Bar (CSS Grid) */}
                {/* Sub-Header Row: Channel Operations + Sync Status, Active Users, Active Integrations (Label: Value) | Date Filter */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 0.5, flexWrap: 'wrap', gap: 1.5 }}>
                    {/* Left-aligned group: header then status items in a row */}
                    <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 3, flex: { xs: '1 1 100%', md: '0 1 auto' }, justifyContent: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {dashboardView === 'trusted_advisor' ? 'Trusted Advisor' : dashboardView === 'acl' ? 'ACL Access Control' : 'Channel Operations'}
                        </Typography>
                        {/* Sync Status, Active Users, Active Integrations only apply to the Channel Operations view */}
                        {dashboardView === 'channel_ops' && (
                        <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Sync Status:</Typography>
                            <Typography component="span" sx={{ fontWeight: 600, color: syncStatusInfo.textColor, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {loading ? <CircularProgress size={14} sx={{ color: 'text.secondary' }} /> : (
                                    <>
                                        {/* Show icon only for non-connected states; connected status has no dot/icon */}
                                        {(syncStatusInfo.status !== 'Successful' && syncStatusInfo.status !== 'Connected' && syncStatusInfo.status !== 'Active') &&
                                            (React.isValidElement(syncStatusInfo.icon) ? React.cloneElement(syncStatusInfo.icon as React.ReactElement<any>, { sx: { fontSize: 16, color: syncStatusInfo.color } }) : syncStatusInfo.icon)
                                        }
                                        {syncStatusInfo.status}
                                    </>
                                )}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Active Users:</Typography>
                            <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                {loading ? <CircularProgress size={14} sx={{ color: 'text.primary' }} /> : activeUsers.formatted}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Active Integrations:</Typography>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {loading ? <CircularProgress size={14} sx={{ color: 'text.secondary' }} /> : (
                                    (overviewData?.active_integrations && overviewData.active_integrations.length > 0) ? (
                                        <>
                                            {overviewData.active_integrations.slice(0, 4).map((app: any, index: number) => (
                                                <Box key={index} component="img" src={app.app_image} sx={{ width: 20, height: 20, borderRadius: '4px' }} alt={app.app_name || 'App'} />
                                            ))}
                                            {overviewData.active_integrations.length > 4 && (
                                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>+{overviewData.active_integrations.length - 4}</Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.875rem' }}>No integrations</Typography>
                                    )
                                )}
                            </Box>
                        </Box>
                        </>
                        )}
                    </Stack>
                    {/* Dashboard view selector (Prism7, channel-gated) + Date Filter - right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {showPrism7DashboardSelector && (
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <Select
                                    value={dashboardView}
                                    onChange={(e) => setDashboardView(e.target.value as DashboardView)}
                                    sx={{
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        bgcolor: 'background.paper',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
                                    }}
                                >
                                    <MenuItem value="channel_ops" sx={{ fontSize: '0.875rem' }}>Channel Operations</MenuItem>
                                    <MenuItem value="trusted_advisor" sx={{ fontSize: '0.875rem' }}>Trusted Advisor</MenuItem>
                                    <MenuItem value="acl" sx={{ fontSize: '0.875rem' }}>ACL Access Control</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {dashboardView === 'channel_ops' && (
                        <Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => setTimeFilterMenuAnchor(e.currentTarget)}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                borderColor: 'divider',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            startIcon={<CalendarIcon sx={{ fontSize: 20 }} />}
                            endIcon={<ChevronRight sx={{ transform: 'rotate(90deg)', fontSize: '1rem' }} />}
                        >
                            {timeFilter}
                        </Button>
                        <Menu
                            anchorEl={timeFilterMenuAnchor}
                            open={Boolean(timeFilterMenuAnchor)}
                            onClose={() => {
                                setTimeFilterMenuAnchor(null);
                                setShowCustomRange(false);
                                setCustomStartDate('');
                                setCustomEndDate('');
                            }}
                            PaperProps={{ sx: { minWidth: '200px', p: 1 } }}
                            MenuListProps={{ sx: { py: 0.5 } }}
                        >
                            {['All', 'Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Last Month'].map((filter) => (
                                <MenuItem 
                                    key={filter} 
                                    onClick={() => { setTimeFilter(filter); setTimeFilterMenuAnchor(null); }}
                                    selected={filter === timeFilter}
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    {filter}
                                </MenuItem>
                            ))}
                            <MenuItem 
                                onClick={() => setShowCustomRange(!showCustomRange)}
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    fontSize: '0.875rem',
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    mt: 0.5,
                                    pt: 1
                                }}
                            >
                                Custom Range
                                <ChevronRight sx={{ fontSize: '1rem', transform: showCustomRange ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
                            </MenuItem>
                            {showCustomRange && (
                                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            type="date"
                                            label="Start Date"
                                            value={customStartDate}
                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            type="date"
                                            label="End Date"
                                            value={customEndDate}
                                            onChange={(e) => setCustomEndDate(e.target.value)}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => {
                                                if (customStartDate && customEndDate) {
                                                    setTimeFilter(`Custom: ${customStartDate} to ${customEndDate}`);
                                                    setTimeFilterMenuAnchor(null);
                                                    setShowCustomRange(false);
                                                    setCustomStartDate('');
                                                    setCustomEndDate('');
                                                }
                                            }}
                                            disabled={!customStartDate || !customEndDate}
                                            sx={{ 
                                                flex: 1, 
                                                textTransform: 'none', 
                                                bgcolor: '#1976d2',
                                                '&:hover': { bgcolor: '#1565c0' }
                                            }}
                                        >
                                            Apply
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                setShowCustomRange(false);
                                                setCustomStartDate('');
                                                setCustomEndDate('');
                                            }}
                                            sx={{ flex: 1, textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Menu>
                    </Box>
                        )}
                    </Box>
                </Box>

                {dashboardView === 'trusted_advisor' ? (
                    // B-2: channelId passed so the backend can scope data to this channel server-side.
                    <TrustedAdvisorDashboard channelId={channelId} />
                ) : dashboardView === 'acl' ? (
                    <AclAccessControlDashboard channelId={channelId} />
                ) : (
                <>
                {/* Stats Bar: Global MTTR, Draft Utilization, Open Tasks, Approvals, Workflow Runs on one row */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' },
                    gap: 3,
                    mb: 3
                }}>
                    {/* Global MTTR: main value + Avg MTTR line */}
                    <StatMiniCard 
                        title="Global MTTR (All Time)" 
                        content={
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '2.125rem !important' }}>
                                    {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : globalMTTR.formatted}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem !important' }}>
                                    Avg MTTR : <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{loading ? '—' : formatMTTR(businessOutcomeData?.mean_time_to_respond?.value_minutes)}</Box>
                                </Typography>
                            </Box>
                        } 
                    />
                    {/* Draft Utilization: moved next to Global MTTR */}
                    <StatMiniCard 
                        title="Draft Utilization" 
                        content={
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '2.125rem !important' }}>
                                    {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : (businessOutcomeData?.draft_to_sent_ratio?.draft_utilization_percentage ?? 0) + '%'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem !important' }}>Drafts <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{businessOutcomeData?.draft_to_sent_ratio?.total_drafts ?? 0}</Box></Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem !important' }}>Replies Sent <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{businessOutcomeData?.draft_to_sent_ratio?.drafts_sent ?? 0}</Box></Typography>
                                </Box>
                            </Box>
                        } 
                    />
                    {/* Open Tasks: task count card (no View Assignees) */}
                    <StatMiniCard 
                        title="Open Tasks" 
                        content={
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '2.125rem !important' }}>
                                {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : totalOpenTasks}
                            </Typography>
                        } 
                    />
                    {/* Approvals: right of Open Tasks (no View Assignees) */}
                    <StatMiniCard
                        title="Approvals"
                        content={
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '2.125rem !important' }}>
                                {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : totalPendingApprovals}
                            </Typography>
                        }
                    />
                    {/* Workflow Runs: total Business Rules Workflow executions across all flows for the
                        selected time filter. The WHOLE tile (not just the number) is the drill-down
                        target — clicking anywhere on it scrolls down to the per-flow breakdown list at
                        the bottom of this dashboard (workflowRunsSectionRef). The inner number itself
                        stays plain/unstyled-for-click, matching every other StatMiniCard's number
                        (Open Tasks, Approvals, etc.) exactly — only this wrapping Box carries the
                        click/cursor affordance. */}
                    <Box
                        onClick={scrollToWorkflowRuns}
                        title="View runs by workflow"
                        sx={{ cursor: 'pointer' }}
                    >
                        <StatMiniCard
                            title="Workflow Runs"
                            content={
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '2.125rem !important' }}>
                                    {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : totalWorkflowRuns}
                                </Typography>
                            }
                        />
                    </Box>
                </Box>

                {/* 2. Main Dashboard Grid (Charts) - CSS Grid */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, 
                    gap: 3, 
                    mb: 3 
                }}>
                    
                    {/* Throughput: total count on top, timeline chart below (no modal) */}
                    <DashboardCard 
                        title="Message Throughput" 
                        icon={<InboxIcon />}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', minHeight: 280 }}>
                            {loading ? <CenteredLoader /> : (
                                (!throughputData?.email_distribution && !timelineData?.timeline_data?.length) ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center' }}>No email throughput data available</Typography>
                                ) : (
                                    <>
                                        {/* Total count on top */}
                                        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                                            <Typography variant="h5" sx={{ fontWeight: '700 !important', color: 'text.primary', fontSize: '2.125rem !important' }}>
                                                {totalEmails.toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.875rem !important' }}>TOTAL</Typography>
                                        </Box>
                                        {/* Inline timeline chart (same as former modal content) */}
                                        <Box sx={{ flex: 1, minHeight: 220, width: '100%' }}>
                                            {(() => {
                                                const apiTimeFilter = getApiTimeFilter(timeFilter);
                                                let currentStartDate: string | undefined;
                                                let currentEndDate: string | undefined;
                                                if (apiTimeFilter === 'custom' && timeFilter.startsWith('Custom:')) {
                                                    const parts = timeFilter.replace('Custom: ', '').split(' to ');
                                                    if (parts.length === 2) {
                                                        currentStartDate = parts[0];
                                                        currentEndDate = parts[1];
                                                    }
                                                }
                                                return (
                                                    <ChannelDashboardTimelineChart
                                                        channelId={channelId}
                                                        initialFilterType={apiTimeFilter}
                                                        initialStartDate={currentStartDate}
                                                        initialEndDate={currentEndDate}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                    </>
                                )
                            )}
                        </Box>
                    </DashboardCard>

                    {/* Email Topics: react-wordcloud (D3-based); words sized by volume, tooltip shows percentage */}
                    <DashboardCard title="Email Topics" icon={<CloudIcon />}>
                        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden', width: '100%' }}>
                            {loading ? <CircularProgress size={24} sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} /> :
                             (!topicCloudData || topicCloudData.length === 0) ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>No topics detected</Typography>
                             ) : (
                                (() => {
                                    // react-wordcloud expects { text, value }; value drives font size; ensure valid entries
                                    const words = topicCloudData
                                        .filter((t) => t.topic != null && String(t.topic).trim() !== '')
                                        .map((t) => ({
                                            text: String(t.topic).trim(),
                                            value: Math.max(0, Number(t.volume) || 0)
                                        }));
                                    // Guard: library can throw if words empty or dimensions undefined
                                    if (words.length === 0) {
                                        return (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No topics detected</Typography>
                                        );
                                    }
                                    // Tooltip shows topic and percentage (package uses default colors)
                                    const topicMap = new Map(topicCloudData.map((t) => [t.topic, t]));
                                    const getWordTooltip = (word: { text: string; value?: number }) => {
                                        const item = topicMap.get(word.text);
                                        const pct = item?.percentage ?? 0;
                                        return `${word.text}: ${pct}%`;
                                    };
                                    const options = {
                                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                        rotations: 0,
                                        rotationAngles: [0, 0] as [number, number],
                                        fontSizes: [14, 42] as [number, number],
                                        padding: 5,
                                        enableTooltip: false,
                                        deterministic: true
                                    };
                                    const callbacks = {
                                        getWordTooltip
                                    };
                                    // Explicit dimensions and minSize; deferred mount avoids library reading size before container has dimensions
                                    const cloudWidth = 400;
                                    const cloudHeight = 220;
                                    return (
                                        <Box sx={{ width: '100%', minWidth: 0, height: cloudHeight, minHeight: cloudHeight }}>
                                            <EmailTopicsWordCloud
                                                words={words}
                                                options={options}
                                                callbacks={callbacks}
                                                size={[cloudWidth, cloudHeight]}
                                                minSize={[cloudWidth, cloudHeight]}
                                            />
                                        </Box>
                                    );
                                })()
                             )}
                        </Box>
                    </DashboardCard>

                    {/* Emerging Topics: graph only, sorted by message count (high to low), bar color by trend (red negative, green otherwise) */}
                    <DashboardCard title="Emerging Topics (24h)" icon={<TrendingUp />}>
                        {loading ? <CenteredLoader /> : (
                            (!emergingTopicsData || emergingTopicsData.length === 0) ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 180 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No emerging topics detected</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ width: '100%', flex: 1, minHeight: 200 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            layout="vertical"
                                            data={[...emergingTopicsData]
                                                .sort((a, b) => b.current_volume - a.current_volume)
                                                .map((row) => ({
                                                    name: row.topic,
                                                    messages: row.current_volume,
                                                    trend: row.percentage_change
                                                }))}
                                            margin={{ top: 4, right: 8, left: 4, bottom: 28 }}
                                        >
                                            <XAxis
                                                type="number"
                                                tick={{ fontSize: 11, fill: palette.text.secondary }}
                                                axisLine={false}
                                                tickLine={false}
                                                label={{ value: 'Messages count', position: 'insideBottom', offset: -8, style: { fill: palette.text.secondary, fontSize: 11 } }}
                                            />
                                            <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: palette.text.primary, fontWeight: 500 }} axisLine={false} tickLine={false} />
                                            <RechartsTooltip
                                                cursor={{ fill: palette.action.hover }}
                                                contentStyle={{
                                                    backgroundColor: palette.background.paper,
                                                    border: `1px solid ${palette.divider}`,
                                                    borderRadius: 8,
                                                    color: palette.text.primary,
                                                }}
                                                itemStyle={{ color: palette.text.primary }}
                                                labelStyle={{ color: palette.text.secondary }}
                                                formatter={(value: number | undefined, _name: string | undefined, props: { payload?: { trend?: number } }) => [
                                                    `${value ?? 0} messages${props?.payload?.trend != null ? ` (${(props.payload.trend >= 0 ? '+' : '')}${props.payload.trend}% trend)` : ''}`,
                                                    'Messages'
                                                ]}
                                            />
                                            <Bar
                                                dataKey="messages"
                                                radius={[0, 4, 4, 0]}
                                                maxBarSize={24}
                                                shape={(props: unknown) => {
                                                    const p = props as { x?: number; y?: number; width?: number; height?: number; payload?: { trend?: number } };
                                                    const { x = 0, y = 0, width = 0, height = 0, payload } = p;
                                                    const fill = payload?.trend != null && payload.trend < 0 ? palette.error.main : palette.success.main;
                                                    return <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />;
                                                }}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )
                        )}
                    </DashboardCard>
                </Box>

                {/* App Engagement & Usage (merged) left of User Contribution */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, 
                    gap: 3 
                }}>
                    {/* Left column: single card with engagement count + usage distribution */}
                    <HalfCard title="App Engagement & Usage" icon={<AppsOutlinedIcon />}>
                        {loading ? <CenteredLoader /> : (
                            <>
                                {/* App engagement count at top */}
                                <Typography variant="h4" sx={{ mt: '12px !important', fontWeight: '700 !important', color: 'text.primary', fontSize: '2.125rem !important', lineHeight: '1.5 !important' }}>
                                    {appUsageData?.total_app_interactions != null ? appUsageData.total_app_interactions.toLocaleString() : 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem !important', display: 'block', mb: 2 }}>AI-Triggered Actions</Typography>
                                {/* Usage distribution below */}
                                {(!appUsageList || appUsageList.length === 0) ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No app usage data</Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ 
                                            height: 80,
                                            overflowY: 'auto',
                                            pr: appUsageList.length > 2 ? 1 : 0,
                                            '&::-webkit-scrollbar': { width: '6px' },
                                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                                            '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: '3px' }
                                        }}>
                                            {appUsageList.map(app => (
                                                <Box key={app.name} sx={{ mb: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontSize: '0.875rem !important' }}>{app.name}</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.875rem !important' }}>{app.value}%</Typography>
                                                    </Box>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={app.value} 
                                                        sx={{ height: 6, borderRadius: 3, bgcolor: 'divider', '& .MuiLinearProgress-bar': { bgcolor: app.color } }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                        <Button 
                                            fullWidth 
                                            size="small" 
                                            onClick={() => setDrillDownType('apps')}
                                            sx={{ mt: 'auto', bgcolor: 'action.hover', color: 'primary.main', border: '1px solid', borderColor: 'divider', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem !important' }}
                                        >
                                            View App Actions
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    </HalfCard>

                    {/* User Contribution Table - design aligned with AdminHub tables */}
                    <DashboardCard 
                        title="User Contribution" 
                        action={
                            userContributionData.length > 1 ? (
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        value={userContributionFilter}
                                        onChange={(e) => setUserContributionFilter(e.target.value as 'highest' | 'lowest')}
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            height: 28, 
                                            bgcolor: 'background.paper',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
                                        }}
                                        displayEmpty
                                    >
                                        <MenuItem value="highest" sx={{ fontSize: '0.75rem' }}>Highest</MenuItem>
                                        <MenuItem value="lowest" sx={{ fontSize: '0.75rem' }}>Lowest</MenuItem>
                                    </Select>
                                </FormControl>
                            ) : null
                        }
                    >
                        {loading ? <CenteredLoader /> : (
                            <TableContainer
                                component={Paper}
                                sx={{
                                    ...getTableContainerStyles(),
                                    backgroundColor: 'background.paper',
                                    boxShadow: 2,
                                    maxHeight: 220,
                                    overflowX: 'auto',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: 6, height: 6 },
                                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: 1 }
                                }}
                            >
                                <Table stickyHeader size="small" sx={{ minWidth: 520 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 120 }}>User</TableCell>
                                            <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 90 }}>Prism7 Queries</TableCell>
                                            <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 90 }}>Drafts Viewed</TableCell>
                                            <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 90 }}>Drafts Edited</TableCell>
                                            <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 90 }}>Messages Sent</TableCell>
                                            <TableCell sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 100 }}>Topics Addressed</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(!userContributionData || userContributionData.length === 0) ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ ...getTableCellStyles(), py: 3, border: 0 }}>
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                        {userContributionFilter === 'lowest' ? 'No users found with low contribution' : 'No user interactions found'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : userContributionData.map((user) => (
                                            <TableRow key={user.user_name} hover sx={getTableRowStyles()}>
                                                <TableCell sx={getTableCellStyles()}>{user.user_name}</TableCell>
                                                <TableCell align="center" sx={getTableCellStyles()}>
                                                    {user.prism7_queries ?? 0}
                                                </TableCell>
                                                <TableCell align="center" sx={getTableCellStyles()}>{user.drafts_viewed}</TableCell>
                                                <TableCell align="center" sx={getTableCellStyles()}>{user.drafts_edited}</TableCell>
                                                <TableCell align="center" sx={getTableCellStyles()}>{user.messages_sent}</TableCell>
                                                <TableCell sx={getTableCellStyles()}>
                                                    <TopicsList topics={user.topics_addressed} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DashboardCard>

                    {/* Workflow Runs by Flow — business_rules_workflow_count drill-down target for the
                        "Workflow Runs" stat tile above (scrollToWorkflowRuns), and the landing spot
                        when navigating here from the Business Rules Workflow tab's canvas-header
                        "Runs: N" label. Full flow-runs API response surfaced as a table — same
                        TableContainer/Table structure and shared style helpers as the User
                        Contribution table above, for visual consistency with the rest of this
                        dashboard (per admin request, rather than the plain name+count rows this
                        replaces). */}
                    {/* gridColumn: '1 / -1' spans this card across every column of the parent grid (so
                        it fills the dashboard's full width and sits centered as its own row), instead
                        of competing for a single 1fr column slot alongside the chart cards. minWidth: 0
                        still overrides the CSS Grid item's default auto min-width (content-based), so
                        the table can lay out across the full width without the item itself refusing to
                        shrink/grow naturally. Scoped to just this one grid item — the grid's own
                        column definition and every other card (App Engagement & Usage, User
                        Contribution, etc.) are untouched. */}
                    <Box ref={workflowRunsSectionRef} sx={{ minWidth: 0, gridColumn: '1 / -1' }}>
                        <DashboardCard title="Workflow Runs by Flow" icon={<PlayCircleOutlineIcon />}>
                            {loading ? <CenteredLoader /> : (
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        ...getTableContainerStyles(),
                                        backgroundColor: 'background.paper',
                                        boxShadow: 2,
                                        maxHeight: 220,
                                        overflowX: 'auto',
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': { width: 6, height: 6 },
                                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: 1 }
                                    }}
                                >
                                    <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 160 }}>Flow</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 70 }}>Runs</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 90 }}>Completed</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 70 }}>Failed</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 130 }}>Approval Requested</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 110 }}>Avg Duration</TableCell>
                                                <TableCell sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 170 }}>Last Run</TableCell>
                                                <TableCell align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles(), minWidth: 80 }}>Current</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortedFlowRuns.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ ...getTableCellStyles(), py: 3, border: 0 }}>
                                                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                            No workflow runs recorded yet.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : sortedFlowRuns.map((flow) => (
                                                <TableRow key={flow.flow_id} hover sx={getTableRowStyles()}>
                                                    <TableCell sx={getTableCellStyles()}>{flow.flow_name}</TableCell>
                                                    <TableCell align="center" sx={{ ...getTableCellStyles(), fontWeight: 700 }}>{flow.run_count}</TableCell>
                                                    <TableCell align="center" sx={getTableCellStyles()}>{flow.completed_count}</TableCell>
                                                    <TableCell align="center" sx={{ ...getTableCellStyles(), color: flow.failed_count > 0 ? 'error.main' : undefined }}>
                                                        {flow.failed_count}
                                                    </TableCell>
                                                    <TableCell align="center" sx={getTableCellStyles()}>{flow.approval_requested_count}</TableCell>
                                                    <TableCell align="center" sx={getTableCellStyles()}>{formatFlowRunDuration(flow.avg_duration_ms)}</TableCell>
                                                    <TableCell sx={getTableCellStyles()}>
                                                        {flow.last_run_at ? new Date(flow.last_run_at).toLocaleString() : '—'}
                                                    </TableCell>
                                                    <TableCell align="center" sx={getTableCellStyles()}>
                                                        {flow.is_current ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} /> : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </DashboardCard>
                    </Box>
                </Box>
                </>
                )}

            </Box>

            {/* Drill Down Modal (apps, task chart, approval chart only; throughput timeline is inline in card) */}
            <Dialog 
                open={!!drillDownType} 
                onClose={() => setDrillDownType(null)}
                PaperProps={{ 
                    sx: { 
                        borderRadius: '12px', 
                        width: '85%', 
                        maxWidth: '600px',
                        height: 'auto',
                        maxHeight: '90vh'
                    } 
                }}
                slotProps={{
                    backdrop: {
                        sx: { 
                            backdropFilter: 'blur(4px)',
                            touchAction: 'none' 
                        }
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, fontSize: '1rem !important' }}>
                    {drillDownType === 'apps' ? 'App Action Breakdown' : 
                     drillDownType === 'taskChart' ? 'Tasks by User' : 'Pending Approvals by User'}
                    <IconButton size="small" onClick={() => setDrillDownType(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ 
                    height: drillDownType === 'apps' ? 'auto' : 350,
                    overflowY: 'auto',
                    p: 2
                }}>
                                    {drillDownType === 'apps' && (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Tool</TableCell>
                                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Action</TableCell>
                                                        <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Count</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                <TableBody>
                                    {appActionsList.map((row, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{row.tool}</TableCell>
                                            <TableCell>{row.action}</TableCell>
                                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {(drillDownType === 'taskChart' || drillDownType === 'approvalChart') && (
                        <Box sx={{ width: '100%', height: '100%' }}>
                            {/* Header / Legend Visual */}
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                                <Box sx={{ width: 40, height: 12, bgcolor: 'primary.main', borderRadius: '2px' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem !important' }}>
                        {drillDownType === 'taskChart' ? 'Open Tasks' : 'Pending Approvals'}
                    </Typography>
                            </Box>
                            <ResponsiveContainer width="100%" height="90%">
                                    <BarChart 
                                        layout="vertical" 
                                        data={drillDownType === 'taskChart' ? taskUsersData : approvalUsersData}
                                        margin={{ top: 0, left: -20, right: 20, bottom: 0 }}
                                    >
                                        <CartesianGrid horizontal={false} stroke={palette.divider} strokeDasharray="3 3"/>
                                        <XAxis type="number" tick={{ fontSize: 12, fill: palette.text.secondary }} axisLine={false} tickLine={false} />
                                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12, fill: palette.text.primary, fontWeight: 500 }} axisLine={false} tickLine={false} />
                                        <RechartsTooltip 
                                            cursor={{ fill: palette.action.hover }} 
                                            contentStyle={{
                                                backgroundColor: palette.background.paper,
                                                border: `1px solid ${palette.divider}`,
                                                borderRadius: 8,
                                                color: palette.text.primary,
                                                boxShadow: muiTheme.shadows[8],
                                            }}
                                            itemStyle={{ color: palette.text.primary }}
                                            labelStyle={{ color: palette.text.secondary }}
                                            formatter={(value: any) => [value, drillDownType === 'taskChart' ? 'Open Tasks' : 'Approvals']}
                                        />
                                    <Bar 
                                        dataKey="value" 
                                        fill={palette.primary.main}
                                        radius={[0, 4, 4, 0]} 
                                        barSize={20} 
                                    />
                                    </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
            </Box>
        </Drawer>
    );
};

export default ChannelDashboard;
